import { resolveComparisonSystemName } from 'components/equipment-page/comparison-modal-helpers';
import {
  BONUS_LABELS,
  PENALTY_LABELS,
  ScoreBreakdownItem
} from 'components/equipment-page/score-breakdown-labels';
import { SCORE_CATEGORY_DEFINITIONS } from 'components/equipment-page/score-category-labels';
import { SystemOptionType } from 'data/types';
import {
  ductedDuplicateNameIndexes,
  nameWithDuplicateIndex
} from 'utils/duplicate-system-names';
import { coolingRequiredOnOption } from 'utils/required-share-ratio';

export type SystemComparisonSummariesType = Record<string, string>;

export type SystemComparisonSummariesResponseType = {
  summaries: SystemComparisonSummariesType;
};

function buildCategoryScores(system: SystemOptionType) {
  const categoryScores = system.score?.categoryScores;
  if (!categoryScores) return [];
  return SCORE_CATEGORY_DEFINITIONS.map(({ key, label }) => ({
    label,
    value: categoryScores[key]?.score ?? 0
  }));
}

function buildBreakdownItems(
  system: SystemOptionType,
  labels: ReadonlyArray<ScoreBreakdownItem>
) {
  const breakdown = system.score?.penaltiesBonuses.breakdown ?? {};
  return labels
    .map(({ code, label }) => ({
      label,
      value: Math.round((breakdown[code] ?? 0) * 10) / 10
    }))
    .filter((item) => item.value !== 0);
}

function buildCondensers(system: SystemOptionType) {
  return system.configuration.condensers.map((condenser) => ({
    condenserModel: condenser.selectedProduct?.model ?? 'Unknown',
    condenserBtu: condenser.selectedProduct?.coolingBTU ?? 0,
    zones: condenser.zones.map((zone) => ({
      zoneName: zone.name,
      type: zone.type,
      airHandlerModel: zone.selectedProduct?.model ?? 'Unknown',
      airHandlerBtu: zone.selectedProduct?.coolingBTU ?? 0,
      spaces: zone.zoneItems.map((item) => item.name)
    }))
  }));
}

export function buildSystemComparisonPayload(systems: SystemOptionType[]) {
  const nameIndexes = ductedDuplicateNameIndexes(systems);
  return {
    systems: systems.map((system, index) => ({
      systemId: system.id,
      name: nameWithDuplicateIndex(
        resolveComparisonSystemName(system, index),
        nameIndexes.get(system.id)
      ),
      price: system.price,
      btu: system.btu,
      heatingScore: system.heatingScore,
      coolingScore: system.coolingScore,
      coolingRequired: coolingRequiredOnOption(system),
      averageSeer: system.averageSeer,
      totalAnnualCost: system.totalAnnualCost,
      installationDifficulty: system.installationDifficulty ?? null,
      amperage: system.amperage ?? null,
      indoorVisibility: system.indoorVisibility ?? null,
      outdoorVisibility: system.outdoorVisibility ?? null,
      inStock: system.inStock,
      overallScore: system.score?.total ?? null,
      categoryScores: buildCategoryScores(system),
      penalties: buildBreakdownItems(system, PENALTY_LABELS),
      bonuses: buildBreakdownItems(system, BONUS_LABELS),
      condensers: buildCondensers(system)
    }))
  };
}

export function buildSystemComparisonKey(systems: SystemOptionType[]) {
  return buildSystemComparisonPayload(systems)
    .systems.map((system) => JSON.stringify(system))
    .sort()
    .join('|');
}
